import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Play, Pause, Volume2, VolumeX } from "lucide-react";
import { MaskedLines, Marquee, Reveal, SectionHead } from "../components/Reveal";
import { BrandMark } from "../components/Emblem";
import { COMPANY, IMAGES, GALLERY_ITEMS, PRINCIPLES } from "../data/site";
import DivisionCards from "../components/DivisionCards";
import useSeo from "../components/Seo";

const EASE = [0.16, 1, 0.3, 1];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-end overflow-hidden max-lg:min-h-0 max-lg:flex-col max-lg:items-stretch" data-testid="hero-section">
      <div className="max-lg:relative max-lg:h-[36vh] max-lg:w-full max-lg:overflow-hidden lg:contents">
        <motion.div className="hero-parallax absolute inset-0" style={{ y: imgY }}>
          <motion.video
            src="/reel-web.mp4"
            poster={IMAGES.hero}
            className="hero-img-mobile h-[115%] w-full object-cover"
            style={{ filter: "grayscale(0.45) contrast(1.08) brightness(0.72) sepia(0.28)" }}
            initial={{ scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.2, ease: EASE }}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label={IMAGES.heroAlt}
            data-testid="hero-video"
          />
        </motion.div>
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-ink to-transparent lg:hidden" aria-hidden="true" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/30 max-lg:hidden" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-transparent max-lg:hidden" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-48 max-lg:pb-20 max-lg:pt-12 lg:px-10">
        <motion.div
          className="mb-12 flex items-center gap-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <BrandMark className="h-16 w-16 sm:h-20 sm:w-20" />
          <div className="h-12 w-px bg-gold/40" />
          <p className="overline-gold max-w-[260px] leading-[1.9] sm:max-w-none">
            {COMPANY.name}
          </p>
        </motion.div>

        <motion.div
          className="mb-9 h-px w-28 bg-gold/60"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 0.35 }}
          style={{ transformOrigin: "left" }}
        />

        <h1 className="font-heading text-4xl font-medium leading-[1.04] tracking-[-0.01em] text-foreground sm:text-6xl lg:text-7xl" data-testid="hero-headline">
          <MaskedLines
            delay={0.45}
            lines={[
              <>Strength in Every <em className="italic text-gold-light">Journey.</em>&nbsp;</>,
              <>Reliability in Every <em className="italic text-gold-light">Mile.</em></>,
            ]}
          />
        </h1>

        <motion.p
          className="mt-10 max-w-xl text-sm leading-[1.9] text-foreground/70 sm:text-base"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1 }}
        >
          {COMPANY.intro}
        </motion.p>

        <motion.div
          className="mt-14 flex flex-col gap-4 sm:flex-row"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
        >
          <Link to="/quote" className="btn-solid group" data-testid="hero-quote-cta">
            Request a Quote
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
          <Link to="/fleet" className="btn-gold group" data-testid="hero-services-cta">
            Explore Our Fleet
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <motion.div
          className="mt-16 flex flex-wrap items-center gap-x-10 gap-y-3 border-t border-foreground/10 pt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          data-testid="hero-trust-strip"
        >
          <span className="text-[10px] font-semibold uppercase tracking-[0.26em] text-foreground/60">
            HQ — Bengaluru, Karnataka <span className="mx-2 text-gold/70">·</span> Pan-India Network <span className="mx-2 text-gold/70">·</span> Incorporated 2026
          </span>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-0 right-6 hidden h-24 w-px bg-gold/40 lg:right-10 lg:block"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 1.2, delay: 1.6 }}
        style={{ transformOrigin: "top" }}
      />
    </section>
  );
}

function DriversSection() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.muted = false;
      setMuted(false);
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section className="border-b border-gold/10 py-24 sm:py-32" data-testid="home-drivers-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-10">
        <Reveal className="order-2 lg:order-1 lg:col-span-5">
          <div className="relative mx-auto max-w-xs border border-gold/20 bg-surface p-2 sm:max-w-sm" data-testid="drivers-film-frame">
            <video
              ref={videoRef}
              data-testid="drivers-film-video"
              className="aspect-[9/16] w-full object-cover"
              playsInline
              preload="metadata"
              poster="/drivers-film-poster.webp"
              src="/drivers-film-web.mp4"
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
            />
            <button
              type="button"
              data-testid="drivers-film-play-toggle"
              onClick={togglePlay}
              aria-label={playing ? "Pause driver film" : "Play driver film with sound"}
              className="absolute inset-2 flex items-center justify-center bg-ink/40 transition-opacity duration-300"
              style={{ opacity: playing ? 0 : 1 }}
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/60 bg-ink/70 backdrop-blur-sm">
                {playing ? <Pause className="h-6 w-6 text-gold-light" /> : <Play className="ml-1 h-6 w-6 text-gold-light" />}
              </span>
            </button>
            {playing && (
              <button
                type="button"
                data-testid="drivers-film-mute-toggle"
                onClick={toggleMute}
                aria-label={muted ? "Unmute driver film" : "Mute driver film"}
                className="absolute bottom-6 right-6 flex h-10 w-10 items-center justify-center rounded-full border border-gold/50 bg-ink/70 backdrop-blur-sm"
              >
                {muted ? <VolumeX className="h-4 w-4 text-gold-light" /> : <Volume2 className="h-4 w-4 text-gold-light" />}
              </button>
            )}
            <p className="mt-3 pb-1 text-center text-[10px] font-semibold uppercase tracking-[0.24em] text-gold/80">
              {playing ? "Sound on — our drivers, in their own words" : "Tap to play with sound"}
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="order-1 lg:order-2 lg:col-span-7">
          <p className="overline-gold">Our Drivers</p>
          <h2 className="mt-5 font-heading text-3xl leading-[1.12] text-foreground sm:text-5xl">
            The strength behind <em className="italic text-gold-light">every mile</em>
          </h2>
          <p className="mt-6 max-w-xl text-base leading-[1.85] text-muted-foreground">
            Behind every consignment is a driver who treats it like his own. This 60-second film
            honours the men behind our wheels — their discipline, their pride, and the journeys
            they carry for our customers, day and night.
          </p>
          <Link
            to="/gallery"
            data-testid="drivers-gallery-cta"
            className="mt-9 inline-flex items-center gap-2 border-t border-gold/25 pt-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold transition-colors hover:text-gold-light"
          >
            Meet Them In Our Gallery <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function ReelSection() {
  return (
    <section className="border-y border-gold/10 bg-surface py-24 max-lg:pb-40 sm:py-32" data-testid="home-reel-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-10">
        <Reveal className="lg:col-span-7">
          <p className="overline-gold">Real Operations</p>
          <h2 className="mt-5 font-heading text-3xl leading-[1.12] text-foreground sm:text-5xl">
            Real fleet. <em className="italic text-gold-light">Real people.</em>
          </h2>
          <p className="mt-6 max-w-xl text-base leading-[1.85] text-muted-foreground">
            Straight from our yard and our routes — our own driver, our own truck, our own work.
            No stock footage, no library clips. This is The Royal Mahaveer Roadlines Pvt Ltd at work.
          </p>
          <Link
            to="/gallery"
            data-testid="reel-gallery-cta"
            className="mt-9 inline-flex items-center gap-2 border-t border-gold/25 pt-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold transition-colors hover:text-gold-light"
          >
            See Our Gallery <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="relative mx-auto max-w-xs border border-gold/20 bg-ink p-2 sm:max-w-sm" data-testid="reel-frame">
            <video
              data-testid="reel-video"
              className="aspect-[9/16] w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster="/reel-poster.webp"
              src="/reel-web.mp4"
            />
            <p className="mt-3 pb-1 text-center text-[10px] font-semibold uppercase tracking-[0.24em] text-gold/80">
              Shot on our own operations
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function AboutTeaser() {
  return (
    <section className="py-24 sm:py-32" data-testid="home-about-section">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <div>
          <SectionHead
            overline="Chapter 01 — Who We Are"
            title={<>A transportation partner built on <em className="italic text-gold-light">discipline</em> and trust</>}
          />
          <Reveal delay={0.15}>
            <p className="mt-8 text-sm leading-relaxed text-muted-foreground sm:text-base">
              The Royal Mahaveer Roadlines Pvt Ltd is a professional road transportation and
              logistics company. We focus on dependable transportation solutions — moving cargo,
              commercial vehicles and heavy loads with responsibility and care.
            </p>
            <p className="mt-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Our aim is simple: long-term relationships built through professional service,
              responsible operations and the reliable movement of goods and vehicles.
            </p>
            <p className="mt-7 border-l-2 border-gold/60 pl-5 font-heading text-base italic leading-relaxed text-foreground sm:text-lg">
              Our vision — to grow into one of India's most dependable transportation ecosystems:
              goods and passenger mobility under one trusted name.
            </p>
            <Link to="/about" className="btn-gold mt-10" data-testid="home-about-cta">
              About the Company <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.2} className="group relative">
          <div className="overflow-hidden border border-gold/20">
            <img src={IMAGES.about} alt={IMAGES.aboutAlt} className="img-spotlight aspect-[4/5] w-full object-cover sm:aspect-[5/5]" loading="lazy" />
          </div>
          <div className="absolute -bottom-5 -left-5 hidden border border-gold/40 bg-ink px-6 py-4 sm:block">
            <p className="font-heading text-sm italic text-muted-foreground">Strength. Reliability. Movement.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DivisionsSection() {
  return (
    <section className="border-t border-gold/10 bg-surface py-24 sm:py-32" data-testid="home-divisions-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHead
          overline="Chapter 02 — What We Do"
          title={<>Five divisions. <em className="italic text-gold-light">One standard.</em></>}
          desc="Every transportation requirement follows one of five paths — goods & specialized, liquid, building & infrastructure, passenger transport, or future mobility. Choose yours."
        />
        <div className="mt-16">
          <DivisionCards />
        </div>
      </div>
    </section>
  );
}

function GalleryTeaser() {
  const featured = ["/fleet/hero-night.webp", "/fleet/heavy-truck.webp", "/fleet/tanker-trailer.webp", "/fleet/multiaxle-new.webp"];
  const shots = featured.map((img) => GALLERY_ITEMS.find((g) => g.image === img)).filter(Boolean);
  return (
    <section className="border-t border-gold/10 py-24 sm:py-32" data-testid="home-gallery-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <SectionHead
            overline="Chapter 03 — Our Gallery"
            title={<>Real operations, <em className="italic text-gold-light">on record</em></>}
            desc="Only genuine photographs of our own vehicles, yard and daily work — never stock imagery."
          />
          <Reveal delay={0.2}>
            <Link to="/gallery" className="btn-gold shrink-0" data-testid="home-gallery-cta">
              View the Full Gallery <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-16 grid gap-px border border-gold/15 bg-gold/15 sm:grid-cols-2 lg:grid-cols-4">
          {shots.map((g, i) => (
            <Reveal key={g.image} delay={0.06 * i}>
              <Link to="/gallery" className="group block overflow-hidden" data-testid={`home-gallery-shot-${i}`}>
                <img src={g.image} alt={`${g.title} — ${COMPANY.name}`} className="img-spotlight aspect-[4/3] w-full object-cover" loading="lazy" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PrinciplesStrip() {
  return (
    <section className="border-t border-gold/10 py-24 sm:py-32" data-testid="home-principles-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHead
          overline="Chapter 04 — Our Principles"
          title={<>Why businesses <em className="italic text-gold-light">choose us</em></>}
        />
        <div className="mt-16 grid gap-x-12 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.num} delay={0.07 * i}>
              <div className="group pt-6" data-testid={`home-principle-${p.num}`}>
                <div className="h-px w-14 bg-gold/25 transition-all duration-500 group-hover:w-24 group-hover:bg-gold/60" />
                <span className="mt-6 block font-heading text-3xl text-gold/70 transition-colors duration-300 group-hover:text-gold">{p.num}</span>
                <h3 className="mt-4 text-sm font-bold uppercase tracking-[0.22em] text-foreground">{p.title}</h3>
                <p className="mt-3 text-sm leading-[1.8] text-muted-foreground">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="relative overflow-hidden border-t border-gold/15 py-24 sm:py-32" data-testid="home-cta-section">
      <img
        src={IMAGES.network}
        alt={IMAGES.networkAlt}
        className="absolute inset-0 h-full w-full object-cover opacity-25"
        style={{ filter: "grayscale(0.6) brightness(0.5) sepia(0.3)" }}
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink" />
      <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
        <Reveal>
          <BrandMark className="mx-auto h-16 w-16" />
          <h2 className="mt-8 font-heading text-3xl font-medium leading-tight text-foreground sm:text-5xl">
            Ready to move with <em className="italic text-gold-light">certainty?</em>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Tell us what needs to move. We will respond with a clear, professional transportation plan.
          </p>
          <Link to="/quote" className="btn-solid mt-10" data-testid="home-cta-quote">
            Request a Quote <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo("Road Transportation & Logistics", "Bengaluru transport company: open trucks, tippers, drinking-water tankers, JCB backhoe hire and heavy equipment. 24/7 service. Call +91 97401 73666.");
  return (
    <>
      <Hero />
      <ReelSection />
      <DriversSection />
      <Marquee />
      <AboutTeaser />
      <DivisionsSection />
      <GalleryTeaser />
      <PrinciplesStrip />
      <CtaBand />
    </>
  );
}
